import * as React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useMutation } from "react-query";
import { LoadingButton } from "@mui/lab";
import { WebhookSubscription, WebhookSubscriptionsService } from "gen/clients";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { HeadersField } from "components/formikFields/HeadersField/HeadersField";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { TemplateVariablesPopover } from "components/TemplateVariablesPopover/TemplateVariablesPopover";
import { describeApplicationEventType } from "utils/applicationEventUtils";
import { hasUniqueHeaderKeys, webhookValidationSchemaFields } from "./webhookFormUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  webhook: WebhookSubscription;
  onSuccess: () => void;
}

enum FieldName {
  NAME = "name",
  CALLBACK_URL = "callbackUrl",
  TIMEOUT_SECONDS = "timeoutSeconds",
  HEADERS = "headers",
  PAYLOAD_TEMPLATE = "payloadTemplate",
  NOTES = "notes"
}

interface FormValues {
  [FieldName.NAME]: string;
  [FieldName.CALLBACK_URL]: string;
  [FieldName.TIMEOUT_SECONDS]: number | "";
  [FieldName.HEADERS]: Record<string, string>;
  [FieldName.PAYLOAD_TEMPLATE]: string;
  [FieldName.NOTES]: string;
}

const validationSchema = Yup.object({ ...webhookValidationSchemaFields });

const EditWebhookDialog: React.FC<Props> = ({ open, onClose, webhook, onSuccess }) => {
  const updateMutation = useMutation(
    (values: FormValues) => {
      const headers = values[FieldName.HEADERS];
      return WebhookSubscriptionsService.updateWebhookSubscription({
        subscriptionId: webhook.id,
        requestBody: {
          name: values[FieldName.NAME],
          callbackUrl: values[FieldName.CALLBACK_URL],
          timeoutSeconds:
            values[FieldName.TIMEOUT_SECONDS] === "" ? undefined : Number(values[FieldName.TIMEOUT_SECONDS]),
          headers: headers && Object.keys(headers).length > 0 ? headers : undefined,
          payloadTemplate: values[FieldName.PAYLOAD_TEMPLATE] || undefined,
          notes: values[FieldName.NOTES] || undefined
        }
      });
    },
    {
      onSuccess: () => {
        onSuccess();
        onClose();
      }
    }
  );

  const initialValues: FormValues = {
    [FieldName.NAME]: webhook.name,
    [FieldName.CALLBACK_URL]: webhook.callbackUrl,
    [FieldName.TIMEOUT_SECONDS]: webhook.timeoutSeconds,
    [FieldName.HEADERS]: webhook.headers || {},
    [FieldName.PAYLOAD_TEMPLATE]: webhook.payloadTemplate || "",
    [FieldName.NOTES]: webhook.notes || ""
  };

  React.useEffect(() => {
    if (open) {
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Webhook</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, helpers) => {
          if (!hasUniqueHeaderKeys(values[FieldName.HEADERS])) {
            helpers.setSubmitting(false);
            return;
          }
          try {
            await updateMutation.mutateAsync(values);
          } catch {
            // error state is surfaced via updateMutation.error
          }
        }}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form autoComplete="off" noValidate>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {updateMutation.error && <ApiErrorMessage apiError={updateMutation.error} />}

                <TextInputField
                  name="eventType"
                  label="Event Type"
                  value={describeApplicationEventType(webhook.eventType)}
                  disabled
                  helperText="Event type cannot be changed"
                />

                <TextInputField name={FieldName.NAME} label="Name" required />

                <TextInputField
                  name={FieldName.CALLBACK_URL}
                  label="Callback URL"
                  required
                  helperText="HTTPS URL invoked when the event fires"
                />

                <HeadersField
                  name={FieldName.HEADERS}
                  label="Request Headers"
                  helperText="Optional static headers added to every webhook invocation"
                />

                <Box>
                  <TextInputField
                    name={FieldName.PAYLOAD_TEMPLATE}
                    label="Payload Template"
                    multiline
                    minRows={4}
                    variant="outlined"
                    required
                    helperText="Request payload template with {{variable}} substitution."
                    InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.875rem" } }}
                  />
                  <TemplateVariablesPopover eventType={webhook.eventType} />
                </Box>

                <TextInputField name={FieldName.NOTES} label="Notes" />

                <TextInputField
                  name={FieldName.TIMEOUT_SECONDS}
                  label="Timeout (seconds)"
                  type="number"
                  inputProps={{ min: 1, max: 25 }}
                  helperText="Between 1 and 25 seconds"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                Save
              </LoadingButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export { EditWebhookDialog };
